import type { LinkProps } from 'expo-router'
import type { ReactElement } from 'react'

import type { TNode } from './contentHtmlParser'

type TContentTypeProps<Type extends string> = { type: Type; className?: string }
type TContentSourceProps = { source: string }

/*******************
 * Inline elements *
 *******************/

type TContentNlValue = TContentTypeProps<'nl'>
export type TContentTextValue = TContentTypeProps<'text'> & {
  format?: 'bold' | 'italic' | 'bold-italic' | 'normal'
}
export type TContentLinkValue = TContentTypeProps<'link'> &
  Pick<LinkProps, 'href' | 'target'> &
  Omit<TContentTextValue, 'type'>
type TContentInlineValue = TContentLinkValue | TContentNlValue | TContentTextValue

/***********************
 * Text block elements *
 ***********************/

export type TContentPValue = TContentTypeProps<'p'> & {
  children: TContentInlineValue[]
}
export type TContentHeadingValue = TContentTypeProps<'heading'> & {
  children: TContentInlineValue[]
}
type TContentTextBlockValue = TContentPValue | TContentHeadingValue

/***************************
 * Text container elements *
 ***************************/

export type TContentImportantValue = TContentTypeProps<'important'> & {
  children: TContentPValue[]
}
export type TContentQuoteValue = TContentTypeProps<'quote'> & {
  children: TContentPValue[]
}
export type TContentSummaryValue = TContentTypeProps<'summary'> & {
  children: string[]
}

export type TContentListValue = TContentTypeProps<'list'> & {
  data?: TNode[]
  listType: 'number' | 'bullet'
}

export type TContentLiValue = TContentTypeProps<'li'> & { listMarker?: string }

export type TContentBlockquoteValue = TContentTypeProps<'blockquote'> & {
  children: TNode[]
}

export type TContentDetailsValue = TContentTypeProps<'details'> & {
  children: TNode[]
  /** Mirrors the HTML `open` attribute: whether the details are initially expanded */
  open?: boolean
}

type TContentTextContainerValue =
  | TContentImportantValue
  | TContentQuoteValue
  | TContentListValue
  | TContentLiValue
  | TContentBlockquoteValue
  | TContentDetailsValue
  | TContentSummaryValue
/******************
 * Media elements *
 ******************/

export type TContentAudioValue = TContentTypeProps<'audio'> &
  TContentSourceProps & {
    title: string
  }
export type TContentYoutubeValue = TContentTypeProps<'youtube'> & {
  youtubeId: string
}
type TContentImgValue = TContentTypeProps<'img'> &
  TContentSourceProps & {
    /** The image will take 100% width as long as it is not more that the provided width */
    width: number
    /** Must respect image aspect ratio */
    height: number
  }

export type TContentIframeValue = TContentTypeProps<'iframe'>

type TContentMediaValue = TContentAudioValue | TContentYoutubeValue | TContentImgValue | TContentIframeValue

/************************/

type TContentAnyValue = TContentInlineValue | TContentTextBlockValue | TContentTextContainerValue | TContentMediaValue

export type TContentType = TContentAnyValue['type']

export type TContentValueToProps<Value extends TContentAnyValue> = Omit<Value, 'children' | 'type'> &
  (Value extends { children: unknown[] }
    ? {
        children: string | ReactElement | (string | ReactElement)[]
      }
    : TNoMoreProps)
